import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { LICENSE_REQUIRED_EVENT, type LicenseView } from '../api/license';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

// Helper to validate token is not null, undefined, or literal strings
const isValidToken = (token: string | null): token is string => {
  return token !== null &&
    token !== undefined &&
    token !== 'null' &&
    token !== 'undefined' &&
    token.trim() !== '';
};

// Get valid token from storage
const getStoredToken = (key: string): string | null => {
  const token = localStorage.getItem(key);
  return isValidToken(token) ? token : null;
};

interface User {
  id: number;
  email: string;
  username: string;
  first_name: string;
  last_name: string;
  role: string;
  status: string;
  tenant_id: string;
  last_login: string | null;
  created_at: string;
  /** An administrator reset the password; the console asks for a new one. */
  must_change_password?: boolean;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  needsSetup: boolean;
  /**
   * The signed-in user's permission codes. Empty until permissions have been
   * loaded from /api/me/permissions. Use hasPermission() to check rather than
   * scanning the array — the helper is null-safe and reads cleanly in JSX.
   */
  permissions: string[];
  /** True when the user holds the given permission code. False until perms load. */
  hasPermission: (code: string) => boolean;
  /** True once /api/me/permissions has answered (or failed) for this user. */
  permissionsLoaded: boolean;
  /**
   * The on-prem licence (null until known, or when the server is older than
   * licensing). `enabled: false` means licensing is off and nothing is gated.
   */
  license: LicenseView | null;
  /** Re-read the licence, e.g. after activating a key. */
  reloadLicense: () => Promise<void>;
  setLicense: (license: LicenseView | null) => void;
  /** Active tenant slug from JWT (mirror of user.tenant_id, null when no user). */
  tenantSlug: string | null;
  /**
   * Authenticate against a specific tenant. tenantSlug is REQUIRED for
   * root-domain deploys (app.example.com, localhost, dev) and ignored
   * server-side when the request hits a tenant subdomain (acme.example.com)
   * — in the subdomain case the resolver pulls the slug from the Host
   * header. Always send what we know; the backend reconciles.
   */
  login: (email: string, password: string, tenantSlug: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  refreshToken: () => Promise<boolean>;
  recheckSetup: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(getStoredToken('dsshield_token'));
  const [refreshTokenStr, setRefreshTokenStr] = useState<string | null>(getStoredToken('dsshield_refresh_token'));
  const [isLoading, setIsLoading] = useState(true);
  const [needsSetup, setNeedsSetup] = useState(false);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);
  const [license, setLicense] = useState<LicenseView | null>(null);

  // Load /api/me/permissions whenever the user changes. Phase 0 foundation
  // — drives <Can>, RouteGuard, and sidebar visibility throughout the app.
  // Failures (e.g. backend hasn't shipped the endpoint yet, or RBAC not
  // seeded) leave permissions as []; gated UI hides itself, but the rest
  // of the app keeps working.
  useEffect(() => {
    if (!user || !token) {
      setPermissions([]);
      setPermissionsLoaded(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/me/permissions`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (!res.ok) {
          if (!cancelled) setPermissions([]);
          return;
        }
        const data = await res.json();
        if (!cancelled) setPermissions(Array.isArray(data?.codes) ? data.codes : []);
      } catch {
        if (!cancelled) setPermissions([]);
      } finally {
        if (!cancelled) setPermissionsLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [user, token]);

  // The on-prem licence. /api/license answers without a licence, so it can
  // explain a block. A server without licensing answers {enabled: false};
  // an older server without the route leaves it null (nothing is gated).
  const reloadLicense = useCallback(async () => {
    if (!token) {
      setLicense(null);
      return;
    }
    try {
      const res = await fetch(`${API_BASE}/license`, { headers: { 'Authorization': `Bearer ${token}` } });
      setLicense(res.ok ? await res.json() : null);
    } catch {
      // Keep what we had: a network blip must not lift or impose the gate.
    }
  }, [token]);

  useEffect(() => {
    if (!user || !token) {
      setLicense(null);
      return;
    }
    reloadLicense();
  }, [user, token, reloadLicense]);

  // api/client.ts fires this when a request is refused for the licence.
  useEffect(() => {
    const onRequired = () => { reloadLicense(); };
    window.addEventListener(LICENSE_REQUIRED_EVENT, onRequired);
    return () => window.removeEventListener(LICENSE_REQUIRED_EVENT, onRequired);
  }, [reloadLicense]);

  const hasPermission = (code: string): boolean => permissions.includes(code);

  // Check setup status + auth on mount
  useEffect(() => {
    const initAuth = async () => {
      try {
        // First check if system is initialized
        const setupRes = await fetch(`${API_BASE}/auth/setup/status`);
        if (setupRes.ok) {
          const setupData = await setupRes.json();
          if (!setupData.initialized) {
            setNeedsSetup(true);
            setIsLoading(false);
            return;
          }
        }
      } catch {
        // If setup check fails, proceed normally (API might not have the endpoint yet)
      }

      if (token) {
        try {
          const response = await fetch(`${API_BASE}/auth/me`, {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          });

          if (response.ok) {
            const userData = await response.json();
            setUser(userData);
          } else if (response.status === 401 && refreshTokenStr) {
            // Try to refresh token
            const refreshed = await refreshToken();
            if (!refreshed) {
              clearAuth();
            }
          } else {
            clearAuth();
          }
        } catch (error) {
          console.error('Auth check failed:', error);
          clearAuth();
        }
      }
      setIsLoading(false);
    };

    initAuth();
  }, []);

  const clearAuth = () => {
    setUser(null);
    setToken(null);
    setRefreshTokenStr(null);
    localStorage.removeItem('dsshield_token');
    localStorage.removeItem('dsshield_refresh_token');
  };

  // A tab that saw an empty installation must not keep showing first-run
  // setup after it was completed elsewhere: check again whenever the tab
  // becomes visible.
  useEffect(() => {
    if (!needsSetup) return;
    const onVisible = async () => {
      if (document.visibilityState !== 'visible') return;
      try {
        const res = await fetch(`${API_BASE}/auth/setup/status`);
        if (res.ok && (await res.json())?.initialized === true) setNeedsSetup(false);
      } catch {
        // Keep the form; the next visibility change tries again.
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [needsSetup]);

  const recheckSetup = () => {
    setNeedsSetup(false);
    setIsLoading(true);
    // Re-trigger the auth check
    const storedToken = getStoredToken('dsshield_token');
    if (storedToken) {
      setToken(storedToken);
    }
    // Re-run init by toggling loading
    setTimeout(async () => {
      if (storedToken) {
        try {
          const response = await fetch(`${API_BASE}/auth/me`, {
            headers: { 'Authorization': `Bearer ${storedToken}` },
          });
          if (response.ok) {
            setUser(await response.json());
          }
        } catch { }
      }
      setIsLoading(false);
    }, 100);
  };

  const login = async (email: string, password: string, tenantSlug: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        // tenant_slug is sent on every request. The backend prefers the
        // Host-header subdomain when present and falls back to this
        // body field — so we always send it and let the server pick.
        body: JSON.stringify({ email, password, tenant_slug: tenantSlug }),
      });

      const data = await response.json();

      if (response.ok) {
        setToken(data.token);
        setRefreshTokenStr(data.refresh_token);
        setUser(data.user);
        if (data.license) setLicense(data.license);
        localStorage.setItem('dsshield_token', data.token);
        localStorage.setItem('dsshield_refresh_token', data.refresh_token);
        localStorage.setItem('dsshield_tenant', tenantSlug);
        return { success: true };
      } else {
        return { success: false, error: data.error || 'Login failed' };
      }
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  };

  const logout = async () => {
    try {
      if (token) {
        await fetch(`${API_BASE}/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      clearAuth();
    }
  };

  const refreshToken = async (): Promise<boolean> => {
    if (!refreshTokenStr) return false;

    try {
      const response = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh_token: refreshTokenStr }),
      });

      if (response.ok) {
        const data = await response.json();
        setToken(data.token);
        setRefreshTokenStr(data.refresh_token);
        setUser(data.user);
        localStorage.setItem('dsshield_token', data.token);
        localStorage.setItem('dsshield_refresh_token', data.refresh_token);
        return true;
      }
    } catch (error) {
      console.error('Token refresh error:', error);
    }
    return false;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isAuthenticated: !!user && !!token,
        isLoading,
        needsSetup,
        permissions,
        hasPermission,
        permissionsLoaded,
        license,
        reloadLicense,
        setLicense,
        tenantSlug: user?.tenant_id ?? null,
        login,
        logout,
        refreshToken,
        recheckSetup,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
